import {
  Tenor,
  BigNumber,
  Product,
  moment,
  CurrencyPairImpl,
  Precision,
} from "shared/cubiq-model-multi-asset";

const PRODUCT_CODE_FX_REGEX: RegExp = new RegExp("[A-Z]{6}");
const NUMBER_PROPERTIES: Array<string> = ["bidRate", "offerRate", "bidSize", "askSize"];

/**
 * Provides base rate model implementation. The data in this model originates
 * from a published MarketDataEvent and it's attached MarketRate data. It also
 * offers base and nonBase currency detection from the product code.
 *
 * NOTE: Base/non base currency detection should be removed once we have an
 * identifier if this is a FX rate or not.
 */
export class Rate {
  private matchesFXRate: boolean = false;
  private invertedRate: Rate = null;
  public orderIds: Array<number> = [];
  public midRate: string = null;
  public midRateFull: number = null;
  public marketSpotRateDisplay: string;
  public marketNdfRateDisplay: string;
  public invertMarketSpotRateDisplay: string;
  public invertMarketNdfRateDisplay: string;

  // both SPOT + 1m/first future are tradeable
  public marketNdfRateTradeable: boolean | null = null;
  // if true marketNdfRateDisplay + marketNdfRateTradeable are displaying spot rate
  public marketNdfPairSpotOnly: boolean | null = null;
  public productCode: string;

  constructor(
    pair: string | CurrencyPairImpl = null,
    public product: string = null,
    public bidRate: string = null,
    public offerRate: string = null,
    public publishDate: Date = null,
    public tradable: boolean = false,
    public ndfHorizon: boolean = false,
    public valueDate: Date = null,
    public tenor: Tenor = Tenor.EMPTY,
    public providerId: number = null,
    public providerCode: string = null,
    public bidSize: string = null,
    public askSize: string = null,
    public spotDecs: number = 0,
    public fwdDecs: number = 0,
    public invertSpotDecs: number = 0,
    public invertFwdDecs: number = 0,
  ) {
    if (pair instanceof CurrencyPairImpl) {
      let extraDec: number = pair.spotPrecision == Precision.FULL ? 0 : 1;
      let extraInvertDec: number = pair.spotInvertPrecision == Precision.FULL ? 0 : 1;

      Object.assign(this, {
        productCode: pair.base + pair.nonBase,
        spotDecs: pair.rateDecimalsQty + extraDec,
        fwdDecs: pair.fwdRateDecimalsQty + extraDec,
        invertSpotDecs: pair.spotInvertDecimalsQty + extraInvertDec,
        invertFwdDecs: pair.fwdInvertDecimalsQty + extraInvertDec,
      });
    } else {
      this.productCode = pair;
    }

    this.matchesFXRate =
      this.productCode != null ? PRODUCT_CODE_FX_REGEX.test(this.productCode) : false;
    if (bidRate != undefined && offerRate != undefined) {
      let midDecs = this.tenor === Tenor.SPOT ? this.spotDecs : this.fwdDecs;
      let mid: BigNumber = new BigNumber(bidRate).plus(offerRate).dividedBy(2);
      this.midRateFull = mid.toNumber();
      this.midRate = mid.toFixed(midDecs);
    }
  }

  public get key(): string {
    return Rate.getRateKey(this.productCode, this.product, this.tenor, this.valueDate);
  }

  public get horizonKey(): string {
    if (this.ndfHorizon) return [this.productCode, this.product, "HORIZON"].join("-");
    return null;
  }

  public get baseCurrency(): string {
    return this.matchesFXRate ? this.productCode.substring(0, 3) : null;
  }

  public get nonBaseCurrency(): string {
    return this.matchesFXRate ? this.productCode.substring(3) : null;
  }

  public invert(spotRate: Rate = null): Rate {
    if (this.tenor !== Tenor.SPOT && spotRate === null)
      throw new Error(`${this.constructor.name}.invert() called on a fwd rate without a spotRate`);
    else if (this.tenor === Tenor.SPOT && spotRate !== null)
      throw new Error(`${this.constructor.name}.invert() called on a spot rate with a spotRate`);

    if (!this.invertedRate || spotRate != null) {
      let bid: string;
      let offer: string;
      let mid: string;

      if (spotRate === null) {
        bid = new BigNumber("1").dividedBy(this.offerRate).toFixed(this.invertSpotDecs);
        offer = new BigNumber("1").dividedBy(this.bidRate).toFixed(this.invertSpotDecs);
        mid = this.midRate;
      } else {
        bid = new BigNumber("1").dividedBy(this.offerRate).toFixed(this.invertFwdDecs);
        offer = new BigNumber("1").dividedBy(this.bidRate).toFixed(this.invertFwdDecs);
        mid = new BigNumber("1").dividedBy(spotRate.midRate).toFixed(this.invertFwdDecs);
      }

      this.invertedRate = new Rate(
        this.productCode,
        this.product,
        bid,
        offer,
        this.publishDate,
        this.tradable,
        this.ndfHorizon,
        this.valueDate,
        this.tenor,
        this.providerId,
        this.providerCode,
        this.offerRate,
        this.bidSize,
        this.spotDecs,
        this.fwdDecs,
        this.invertSpotDecs,
        this.invertFwdDecs,
      );
      this.invertedRate.invertedRate = this;
    }

    return this.invertedRate;
  }

  public toString(): string {
    let valueDate =
      this.valueDate != null
        ? `${this.valueDate.getDate()}-${
            this.valueDate.getMonth() + 1
          }-${this.valueDate.getFullYear()}`
        : null;
    let tenor = this.tenor != null ? Tenor[this.tenor] : null;

    return `Rate[productCode:${this.productCode},product:${this.product},tenor:${tenor},valueDate:${valueDate}}]`;
  }

  public static getRateKey(
    productCode: string,
    product: string,
    tenor: Tenor = Tenor.EMPTY,
    valueDate: Date = null,
  ): string {
    let localeString: string = "DD-MMM-YY";
    if ((tenor == null || tenor == Tenor.EMPTY) && valueDate != null) {
      let valueDateString = moment(valueDate).utc(true).format(localeString);
      return [productCode, product, valueDateString].join("-");
    }
    return [productCode, product, Tenor[tenor]].join("-");
  }
}
